'use client'

import {
    createPlan,
    getPlan,
    Recurrence,
    updatePlan,
} from '@/lib/api/plans/plan-api'
import { getSystemModules, SystemModule } from '@/lib/api/system-modules/system-modules-api'
import { getPlanTiers, PlanTier } from '@/lib/api/plan-tiers/plan-tiers-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdClose,
    MdExpandLess,
    MdExpandMore,
    MdSave,
    MdViewList,
    MdWidgets,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface PlanFeatures {
    support: boolean
    departments: string
    departmentsUnlimited: boolean
    users: string
    usersUnlimited: boolean
    /** Usuários de IA (`userType AI`). Zero = o plano não inclui. */
    aiUsers: string
    aiUsersUnlimited: boolean
    teleconsultationMinutes: string
    teleconsultationMinutesUnlimited: boolean
    monthlySignDocuments: string
    monthlySignDocumentsUnlimited: boolean
    monthlyFiscalNotes: string
    monthlyFiscalNotesUnlimited: boolean
    storageGb: string
    storageGbUnlimited: boolean
    maxFileSizeMb: string
    maxFileSizeMbUnlimited: boolean
    stores: string
    storesUnlimited: boolean
    /** Vazio: o plano fica fora dos créditos de IA (nada é debitado nem bloqueado). */
    aiCredits: string
    aiCreditsUnlimited: boolean
    /**
     * Limites da videoconferência (MediarMeet). Vazios: o plano não libera vídeo.
     * Nunca ilimitado: o módulo recusa `unlimited` (docs/mediarmeet.md).
     */
    videoMaxParticipants: string
    videoMaxConcurrentRooms: string
    videoMaxRoomMinutes: string
    /** Créditos de vídeo inclusos por mês (1 crédito = 1 minuto por participante). Vazio = zero. */
    videoCredits: string
    /** Sala no domínio do cliente (Cloudflare for SaaS). Só vale com o vídeo liberado. */
    videoCustomDomain: boolean
    /** Gravação das reuniões (minuto gravado cobrado em créditos). Só vale com o vídeo liberado. */
    videoRecording: boolean
    resale: boolean
    customizations: boolean
}

interface PlanFormData {
    name: string
    description: string
    price: string
    isCustom: boolean
    customPrice: string
    private: boolean
    recurrence: Recurrence
    /** Período grátis sugerido ao criar links deste plano, em dias. */
    trialDays: string
    features: PlanFeatures
    tierId: number | null
}

const currencyFormat = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) value = '0'
    value = `${value}`.replace(/[^0-9]+/g, '')
    return mask(value, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const INITIAL_FEATURES: PlanFeatures = {
    support: true,
    departments: '3',
    departmentsUnlimited: false,
    users: '3',
    usersUnlimited: false,
    aiUsers: '0',
    aiUsersUnlimited: false,
    teleconsultationMinutes: '500',
    teleconsultationMinutesUnlimited: false,
    monthlySignDocuments: '10',
    monthlySignDocumentsUnlimited: false,
    monthlyFiscalNotes: '100',
    monthlyFiscalNotesUnlimited: false,
    storageGb: '5',
    storageGbUnlimited: false,
    maxFileSizeMb: '100',
    maxFileSizeMbUnlimited: false,
    stores: '1',
    storesUnlimited: false,
    aiCredits: '',
    aiCreditsUnlimited: false,
    videoMaxParticipants: '',
    videoMaxConcurrentRooms: '',
    videoMaxRoomMinutes: '',
    videoCredits: '',
    videoCustomDomain: false,
    videoRecording: false,
    resale: false,
    customizations: false,
}

const INITIAL_FORM: PlanFormData = {
    name: '',
    description: '',
    price: currencyFormat('0'),
    isCustom: false,
    customPrice: '',
    private: false,
    recurrence: 'MONTHLY',
    trialDays: '',
    features: { ...INITIAL_FEATURES },
    tierId: null,
}

/** Número positivo vira texto; o resto (ausente, zero, `unlimited`) vira vazio. */
function positiveOrEmpty(value: unknown): string {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? String(Math.floor(parsed)) : ''
}

export default function PlanFormPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEdit = !!id

    const [form, setForm] = useState<PlanFormData>(INITIAL_FORM)
    const [systemModules, setSystemModules] = useState<SystemModule[]>([])
    const [tiers, setTiers] = useState<PlanTier[]>([])
    const [additionalModules, setAdditionalModules] = useState<Record<string, string[]>>({})
    const [excludedModules, setExcludedModules] = useState<string[]>([])
    const [expandedTierModules, setExpandedTierModules] = useState<Set<string>>(new Set())
    const [expandedAdditional, setExpandedAdditional] = useState<Set<string>>(new Set())
    const [modulesLoading, setModulesLoading] = useState(true)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        loadModules()
        loadTiers()
    }, [])

    useEffect(() => {
        if (isEdit && id) {
            loadPlan(parseInt(id, 10))
        }
    }, [id])

    const loadModules = async () => {
        try {
            const response = await getSystemModules()
            if (response.success && response.result) {
                setSystemModules(response.result)
            }
        } catch {
            toast.error('Erro ao carregar módulos')
        } finally {
            setModulesLoading(false)
        }
    }

    const loadTiers = async () => {
        try {
            const response = await getPlanTiers()
            if (response.success) {
                setTiers(response.result || [])
            }
        } catch {
            // silently fail
        }
    }

    // Módulos que vêm do tier selecionado (M:N — exatamente os módulos do tier)
    const tierModules = useMemo(() => {
        if (!form.tierId) return []
        const selectedTier = tiers.find(t => t.id === form.tierId)
        if (!selectedTier) return []
        const tierKeys = new Set((selectedTier.modules ?? []).map(m => m.key))
        return systemModules.filter(m => tierKeys.has(m.key))
    }, [form.tierId, tiers, systemModules])

    const tierModuleKeys = useMemo(
        () => new Set(tierModules.map(m => m.key)),
        [tierModules],
    )

    // Módulos disponíveis para adicionar como override (não estão no tier)
    const availableForAdditional = useMemo(
        () => systemModules.filter(m => !tierModuleKeys.has(m.key)),
        [systemModules, tierModuleKeys],
    )

    // Módulos do tier que podem ser excluídos
    const availableForExclusion = useMemo(
        () => tierModules.filter(m => !excludedModules.includes(m.key)),
        [tierModules, excludedModules],
    )

    const toggleAdditionalModule = (mod: SystemModule) => {
        setAdditionalModules(prev => {
            const next = { ...prev }
            if (mod.key in next) {
                delete next[mod.key]
            } else {
                next[mod.key] = []
            }
            return next
        })
    }

    const toggleAdditionalResource = (moduleKey: string, resourceKey: string, mod: SystemModule) => {
        setAdditionalModules(prev => {
            const next = { ...prev }
            const currentResources = next[moduleKey]
            if (!currentResources) return prev

            if (currentResources.length === 0) {
                next[moduleKey] = mod.resources
                    .map(r => r.key)
                    .filter(k => k !== resourceKey)
            } else if (currentResources.includes(resourceKey)) {
                const filtered = currentResources.filter(k => k !== resourceKey)
                if (filtered.length === 0) {
                    delete next[moduleKey]
                } else {
                    next[moduleKey] = filtered
                }
            } else {
                const updated = [...currentResources, resourceKey]
                if (updated.length === mod.resources.length) {
                    next[moduleKey] = []
                } else {
                    next[moduleKey] = updated
                }
            }
            return next
        })
    }

    const isAdditionalResourceSelected = (moduleKey: string, resourceKey: string) => {
        const resources = additionalModules[moduleKey]
        if (!resources) return false
        if (resources.length === 0) return true
        return resources.includes(resourceKey)
    }

    const toggleExcludeModule = (moduleKey: string) => {
        setExcludedModules(prev => {
            if (prev.includes(moduleKey)) {
                return prev.filter(k => k !== moduleKey)
            } else {
                // Ao excluir, remover override de recursos se existir
                setAdditionalModules(am => {
                    const next = { ...am }
                    delete next[moduleKey]
                    return next
                })
                return [...prev, moduleKey]
            }
        })
    }

    // Verifica se um módulo do tier tem override de recursos
    const getTierModuleResources = (moduleKey: string, mod: SystemModule): string[] | null => {
        if (additionalModules[moduleKey] !== undefined) {
            return additionalModules[moduleKey]
        }
        return null // null = todos os recursos (padrão do tier)
    }

    const isTierResourceSelected = (moduleKey: string, resourceKey: string, mod: SystemModule): boolean => {
        const override = additionalModules[moduleKey]
        if (override === undefined) return true // sem override = todos
        if (override.length === 0) return true // [] = todos
        return override.includes(resourceKey)
    }

    const toggleTierResource = (moduleKey: string, resourceKey: string, mod: SystemModule) => {
        setAdditionalModules(prev => {
            const next = { ...prev }
            const currentOverride = next[moduleKey]

            if (currentOverride === undefined) {
                // Sem override: criar override com todos menos o desmarcado
                next[moduleKey] = mod.resources
                    .map(r => r.key)
                    .filter(k => k !== resourceKey)
            } else if (currentOverride.length === 0) {
                // Override com [] (todos): transformar para lista explícita menos o desmarcado
                next[moduleKey] = mod.resources
                    .map(r => r.key)
                    .filter(k => k !== resourceKey)
            } else if (currentOverride.includes(resourceKey)) {
                // Remover recurso
                const filtered = currentOverride.filter(k => k !== resourceKey)
                if (filtered.length === 0) {
                    // Se não sobrou nenhum recurso, excluir o módulo inteiro
                    delete next[moduleKey]
                    setExcludedModules(ex => [...ex, moduleKey])
                } else {
                    next[moduleKey] = filtered
                }
            } else {
                // Adicionar recurso
                const updated = [...currentOverride, resourceKey]
                // Se selecionou todos, remover override (volta ao padrão do tier)
                if (updated.length === mod.resources.length) {
                    delete next[moduleKey]
                } else {
                    next[moduleKey] = updated
                }
            }
            return next
        })
    }

    const getTierModuleResourceCount = (mod: SystemModule): number => {
        const override = additionalModules[mod.key]
        if (override === undefined) return mod.resources.length
        if (override.length === 0) return mod.resources.length
        return override.length
    }

    const loadPlan = async (planId: number) => {
        setLoading(true)
        try {
            const response = await getPlan(planId)
            if (response.success && response.result) {
                const p = response.result
                const f = (p.features || {}) as Record<string, any>

                const features: PlanFeatures = {
                    support: f.support === true,
                    departments:
                        f.departments === 'unlimited'
                            ? ''
                            : String(f.departments || ''),
                    departmentsUnlimited: f.departments === 'unlimited',
                    users:
                        f.users === 'unlimited'
                            ? ''
                            : String(f.users || ''),
                    usersUnlimited: f.users === 'unlimited',
                    aiUsers:
                        f.aiUsers === 'unlimited'
                            ? ''
                            : String(f.aiUsers ?? 0),
                    aiUsersUnlimited: f.aiUsers === 'unlimited',
                    teleconsultationMinutes:
                        f.teleconsultationMinutes === 'unlimited'
                            ? ''
                            : String(f.teleconsultationMinutes || ''),
                    teleconsultationMinutesUnlimited:
                        f.teleconsultationMinutes === 'unlimited',
                    monthlySignDocuments:
                        f.monthlySignDocuments === 'unlimited'
                            ? ''
                            : String(f.monthlySignDocuments || ''),
                    monthlySignDocumentsUnlimited:
                        f.monthlySignDocuments === 'unlimited',
                    monthlyFiscalNotes:
                        f.monthlyFiscalNotes === 'unlimited'
                            ? ''
                            : String(f.monthlyFiscalNotes || ''),
                    monthlyFiscalNotesUnlimited:
                        f.monthlyFiscalNotes === 'unlimited',
                    storageGb:
                        f.storageGb === 'unlimited'
                            ? ''
                            : String(f.storageGb ?? ''),
                    storageGbUnlimited: f.storageGb === 'unlimited',
                    maxFileSizeMb:
                        f.maxFileSizeMb === 'unlimited'
                            ? ''
                            : String(f.maxFileSizeMb ?? ''),
                    maxFileSizeMbUnlimited: f.maxFileSizeMb === 'unlimited',
                    stores:
                        f.stores === 'unlimited'
                            ? ''
                            : String(f.stores || '1'),
                    storesUnlimited:
                        f.stores === 'unlimited',
                    aiCredits:
                        f.aiCredits === undefined || f.aiCredits === null || f.aiCredits === 'unlimited'
                            ? ''
                            : String(f.aiCredits),
                    aiCreditsUnlimited: f.aiCredits === 'unlimited',
                    videoMaxParticipants: positiveOrEmpty(f.videoMaxParticipants),
                    videoMaxConcurrentRooms: positiveOrEmpty(f.videoMaxConcurrentRooms),
                    videoMaxRoomMinutes: positiveOrEmpty(f.videoMaxRoomMinutes),
                    videoCredits: positiveOrEmpty(f.videoCredits),
                    videoCustomDomain: f.videoCustomDomain === true,
                    videoRecording: f.videoRecording === true,
                    resale: f.resale === true,
                    customizations: f.customizations === true,
                }

                setForm({
                    name: p.name || '',
                    description: p.description || '',
                    price: currencyFormat(p.price),
                    isCustom: p.isCustom,
                    customPrice:
                        p.customPrice != null
                            ? currencyFormat(p.customPrice)
                            : '',
                    private: p.private,
                    recurrence: p.recurrence,
                    trialDays: p.trialDays ? String(p.trialDays) : '',
                    features,
                    tierId: p.tierId ?? null,
                })

                if (p.additionalModules && typeof p.additionalModules === 'object') {
                    setAdditionalModules(p.additionalModules as Record<string, string[]>)
                }
                if (p.excludedModules && Array.isArray(p.excludedModules)) {
                    setExcludedModules(p.excludedModules)
                }
            } else {
                toast.error('Plano não encontrado')
                navigate('/plans')
            }
        } catch {
            toast.error('Erro ao carregar plano')
            navigate('/plans')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (
        field: keyof PlanFormData,
        value: string | boolean | number | null
    ) => {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const handleFeatureChange = (
        field: keyof PlanFeatures,
        value: string | boolean
    ) => {
        setForm(prev => ({
            ...prev,
            features: { ...prev.features, [field]: value },
        }))
    }

    const validate = (): boolean => {
        if (!form.name.trim()) {
            toast.error('Nome do plano é obrigatório')
            return false
        }
        if (!form.description.trim()) {
            toast.error('Descrição do plano é obrigatória')
            return false
        }
        const price = parseInt(unMask(form.price, 'currency'), 10)
        if (isNaN(price) || price < 0) {
            toast.error('Preço deve ser um valor válido (>= 0)')
            return false
        }
        const videoLimits = [
            form.features.videoMaxParticipants,
            form.features.videoMaxConcurrentRooms,
            form.features.videoMaxRoomMinutes,
        ].map(value => value.trim())
        const filledVideoLimits = videoLimits.filter(value => value !== '')
        if (filledVideoLimits.length > 0) {
            if (filledVideoLimits.length < 3) {
                toast.error('Videoconferência: preencha os três limites ou deixe os três vazios')
                return false
            }
            if (videoLimits.some(value => !/^\d+$/.test(value) || parseInt(value, 10) < 1)) {
                toast.error('Videoconferência: os limites devem ser números inteiros maiores que zero')
                return false
            }
            const included = form.features.videoCredits.trim()
            if (included !== '' && !/^\d+$/.test(included)) {
                toast.error('Videoconferência: os minutos inclusos devem ser um número inteiro')
                return false
            }
            if (parseInt(videoLimits[0], 10) < 2) {
                toast.error('Videoconferência: a sala precisa aceitar ao menos 2 participantes')
                return false
            }
        }
        if (form.isCustom && form.customPrice) {
            const cp = parseInt(unMask(form.customPrice, 'currency'), 10)
            if (isNaN(cp) || cp < 0) {
                toast.error(
                    'Preço sob medida deve ser um valor válido (>= 0)'
                )
                return false
            }
        }
        return true
    }

    const buildFeaturesObject = (): Record<string, any> => {
        const f = form.features
        return {
            support: f.support,
            departments: f.departmentsUnlimited
                ? 'unlimited'
                : parseInt(f.departments, 10) || 0,
            // Usuários de IA: limite próprio, fora do de usuários. Zero = nenhum.
            aiUsers: f.aiUsersUnlimited
                ? 'unlimited'
                : parseInt(f.aiUsers, 10) || 0,
            users: f.usersUnlimited
                ? 'unlimited'
                : parseInt(f.users, 10) || 0,
            teleconsultationMinutes: f.teleconsultationMinutesUnlimited
                ? 'unlimited'
                : parseInt(f.teleconsultationMinutes, 10) || 0,
            monthlySignDocuments: f.monthlySignDocumentsUnlimited
                ? 'unlimited'
                : parseInt(f.monthlySignDocuments, 10) || 0,
            monthlyFiscalNotes: f.monthlyFiscalNotesUnlimited
                ? 'unlimited'
                : parseInt(f.monthlyFiscalNotes, 10) || 0,
            // Espaço de Armazenamento do plano, em GB. É o teto que a
            // organização não consegue ultrapassar sem contratar GB extra.
            storageGb: f.storageGbUnlimited
                ? 'unlimited'
                : parseInt(f.storageGb, 10) || 0,
            // Tamanho máximo por arquivo, em MB. Fica no plano por ser alavanca
            // comercial: plano barato limita o arquivo, plano superior libera.
            maxFileSizeMb: f.maxFileSizeMbUnlimited
                ? 'unlimited'
                : parseInt(f.maxFileSizeMb, 10) || 0,
            stores: f.storesUnlimited
                ? 'unlimited'
                : parseInt(f.stores, 10) || 1,
            // Créditos de IA por mês. Vazio não grava a chave: o plano fica fora
            // dos créditos. Zero gravaria "nenhum crédito" e bloquearia a IA.
            ...(f.aiCreditsUnlimited
                ? { aiCredits: 'unlimited' }
                : f.aiCredits.trim() !== ''
                  ? { aiCredits: Math.max(0, parseInt(f.aiCredits, 10) || 0) }
                  : {}),
            // Videoconferência: os três limites ou nenhum. Sem eles o plano não
            // libera vídeo; nunca existe "ilimitado" aqui.
            ...(f.videoMaxParticipants.trim() !== ''
                ? {
                      videoMaxParticipants: parseInt(f.videoMaxParticipants, 10),
                      videoMaxConcurrentRooms: parseInt(f.videoMaxConcurrentRooms, 10),
                      videoMaxRoomMinutes: parseInt(f.videoMaxRoomMinutes, 10),
                      // Minutos inclusos no mês; o que passar sai dos créditos comprados.
                      videoCredits: Math.max(0, parseInt(f.videoCredits, 10) || 0),
                      ...(f.videoCustomDomain ? { videoCustomDomain: true } : {}),
                      ...(f.videoRecording ? { videoRecording: true } : {}),
                  }
                : {}),
            resale: f.resale,
            customizations: f.customizations,
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) return

        setSaving(true)
        try {
            const priceInCents = parseInt(unMask(form.price, 'currency'), 10) || 0
            const customPriceInCents =
                form.isCustom && form.customPrice
                    ? parseInt(unMask(form.customPrice, 'currency'), 10) || 0
                    : undefined

            const data: any = {
                name: form.name.trim(),
                description: form.description.trim(),
                price: priceInCents,
                isCustom: form.isCustom,
                private: form.private,
                recurrence: form.recurrence,
                // Padrão apenas: quem concede o período grátis é o link de
                // cadastro, que pode dar mais, menos ou nenhum dia.
                trialDays: form.trialDays
                    ? Math.max(parseInt(form.trialDays, 10) || 0, 0)
                    : null,
                features: buildFeaturesObject(),
                tierId: form.tierId,
                additionalModules,
                excludedModules,
            }

            if (customPriceInCents !== undefined) {
                data.customPrice = customPriceInCents
            }

            let response
            if (isEdit && id) {
                response = await updatePlan(parseInt(id, 10), data)
            } else {
                response = await createPlan(data)
            }

            if (response.success) {
                toast.success(
                    isEdit
                        ? 'Plano atualizado com sucesso'
                        : 'Plano criado com sucesso'
                )
                navigate('/plans')
            } else {
                toast.error(
                    response.error ||
                    (isEdit
                        ? 'Erro ao atualizar plano'
                        : 'Erro ao criar plano')
                )
            }
        } catch {
            toast.error(
                isEdit ? 'Erro ao atualizar plano' : 'Erro ao criar plano'
            )
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando plano...
                </p>
            </div>
        )
    }

    const { features } = form

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/plans')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdViewList className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEdit ? 'Editar Plano' : 'Novo Plano'}
                        </h1>
                        <p className="text-sm text-base-content/60 mt-1">
                            {isEdit
                                ? 'Atualize os dados do plano'
                                : 'Configure o novo plano de assinatura'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
                <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-8">
                    {/* Informacoes Basicas */}
                    <div>
                        <h3 className="text-lg font-bold text-base-content mb-4">
                            Informações do Plano
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full md:col-span-2">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome do Plano *
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ex: Plano Básico, Plano Profissional"
                                    className="input input-bordered w-full"
                                    value={form.name}
                                    onChange={e =>
                                        handleChange('name', e.target.value)
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full md:col-span-2">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Descrição *
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full min-h-[100px]"
                                    placeholder="Descreva o que este plano oferece..."
                                    value={form.description}
                                    onChange={e =>
                                        handleChange(
                                            'description',
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Preço Base (R$) *
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full"
                                    value={form.price}
                                    onChange={e =>
                                        handleChange(
                                            'price',
                                            currencyFormat(e.target.value)
                                        )
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Recorrência
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.recurrence}
                                    onChange={e =>
                                        handleChange(
                                            'recurrence',
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="MONTHLY">Mensal</option>
                                    <option value="YEARLY">Anual</option>
                                    <option value="SEMIANNUAL">
                                        Semestral
                                    </option>
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Período grátis padrão
                                    </span>
                                </label>
                                <label className="input input-bordered w-full flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={0}
                                        max={365}
                                        className="grow"
                                        placeholder="0"
                                        value={form.trialDays}
                                        onChange={e =>
                                            handleChange(
                                                'trialDays',
                                                e.target.value
                                            )
                                        }
                                    />
                                    <span className="text-base-content/50 text-sm">
                                        dias
                                    </span>
                                </label>
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        Só preenche o formulário do link de
                                        cadastro. Cada link pode conceder mais,
                                        menos ou nenhum dia.
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Opcoes */}
                    <div>
                        <h3 className="text-lg font-bold text-base-content mb-4">
                            Opções
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={form.private}
                                        onChange={e =>
                                            handleChange(
                                                'private',
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <div>
                                        <span className="label-text font-medium">
                                            Plano Privado
                                        </span>
                                        <p className="text-xs text-base-content/50 mt-0.5">
                                            Não será exibido publicamente
                                        </p>
                                    </div>
                                </label>
                            </div>

                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={form.isCustom}
                                        onChange={e =>
                                            handleChange(
                                                'isCustom',
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <div>
                                        <span className="label-text font-medium">
                                            Sob Medida
                                        </span>
                                        <p className="text-xs text-base-content/50 mt-0.5">
                                            Permite preço personalizado
                                        </p>
                                    </div>
                                </label>
                            </div>

                            {form.isCustom && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Preço Sob Medida (R$)
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="R$ 0,00"
                                        className="input input-bordered w-full"
                                        value={form.customPrice}
                                        onChange={e =>
                                            handleChange(
                                                'customPrice',
                                                currencyFormat(e.target.value)
                                            )
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Limites do Plano */}
                    <div>
                        <h3 className="text-lg font-bold text-base-content mb-4">
                            Limites do Plano
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[
                                { label: 'Departamentos', field: 'departments' as const, unlimitedField: 'departmentsUnlimited' as const, placeholder: '3' },
                                { label: 'Usuários', field: 'users' as const, unlimitedField: 'usersUnlimited' as const, placeholder: '3' },
                                { label: 'Usuários de IA', field: 'aiUsers' as const, unlimitedField: 'aiUsersUnlimited' as const, placeholder: '0' },
                                { label: 'Minutos de Teleconsulta', field: 'teleconsultationMinutes' as const, unlimitedField: 'teleconsultationMinutesUnlimited' as const, placeholder: '500' },
                                { label: 'Documentos para Assinatura/Mês', field: 'monthlySignDocuments' as const, unlimitedField: 'monthlySignDocumentsUnlimited' as const, placeholder: '10' },
                                { label: 'Notas Fiscais/Mês', field: 'monthlyFiscalNotes' as const, unlimitedField: 'monthlyFiscalNotesUnlimited' as const, placeholder: '100' },
                                { label: 'Lojas Online', field: 'stores' as const, unlimitedField: 'storesUnlimited' as const, placeholder: '1' },
                                { label: 'Armazenamento (GB)', field: 'storageGb' as const, unlimitedField: 'storageGbUnlimited' as const, placeholder: '5' },
                                { label: 'Tamanho máx. por arquivo (MB)', field: 'maxFileSizeMb' as const, unlimitedField: 'maxFileSizeMbUnlimited' as const, placeholder: '100' },
                            ].map(({ label, field, unlimitedField, placeholder }) => (
                                <div key={field} className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">{label}</span>
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder={placeholder}
                                            className="input input-bordered w-full"
                                            value={features[field]}
                                            onChange={e => handleFeatureChange(field, e.target.value)}
                                            disabled={features[unlimitedField]}
                                        />
                                        <label className="label cursor-pointer gap-2 shrink-0">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm checkbox-secondary"
                                                checked={features[unlimitedField]}
                                                onChange={e => handleFeatureChange(unlimitedField, e.target.checked)}
                                            />
                                            <span className="label-text text-xs">Ilimitado</span>
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Créditos de IA */}
                    <div>
                        <h3 className="text-lg font-bold text-base-content mb-1">
                            Créditos de IA
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4 max-w-2xl">
                            Créditos incluídos por mês, que zeram na virada. Acabando, a conta
                            compra mais na Loja do Sistema; sem saldo, a IA para e o atendente
                            de WhatsApp passa a conversa para uma pessoa. Deixe vazio para o
                            plano ficar fora dos créditos: nada é descontado nem bloqueado.
                        </p>
                        <div className="form-control w-full max-w-md">
                            <label className="label" htmlFor="plan-ai-credits">
                                <span className="label-text font-medium">Créditos por mês</span>
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    id="plan-ai-credits"
                                    type="number"
                                    min="0"
                                    placeholder="Fora dos créditos"
                                    className="input input-bordered w-full"
                                    value={features.aiCredits}
                                    onChange={e => handleFeatureChange('aiCredits', e.target.value)}
                                    disabled={features.aiCreditsUnlimited}
                                />
                                <label className="label cursor-pointer gap-2 shrink-0">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm checkbox-secondary"
                                        checked={features.aiCreditsUnlimited}
                                        onChange={e => handleFeatureChange('aiCreditsUnlimited', e.target.checked)}
                                    />
                                    <span className="label-text text-xs">Ilimitado</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Videoconferência */}
                    <div>
                        <h3 className="text-lg font-bold text-base-content mb-1">
                            Videoconferência
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4 max-w-2xl">
                            Limites do plano para as salas de vídeo e os minutos inclusos no mês. Preencha os três para liberar o
                            vídeo, ou deixe os três vazios para o plano não incluir. Não existe
                            opção ilimitada: o consumo acima da cota é cobrado em créditos.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                            <div className="form-control w-full">
                                <label className="label" htmlFor="plan-video-participants">
                                    <span className="label-text font-medium">Participantes por sala</span>
                                </label>
                                <input
                                    id="plan-video-participants"
                                    type="number"
                                    min="1"
                                    step="1"
                                    placeholder="Ex.: 50"
                                    className="input input-bordered w-full"
                                    value={features.videoMaxParticipants}
                                    onChange={e => handleFeatureChange('videoMaxParticipants', e.target.value)}
                                />
                            </div>
                            <div className="form-control w-full">
                                <label className="label" htmlFor="plan-video-rooms">
                                    <span className="label-text font-medium">Salas simultâneas</span>
                                </label>
                                <input
                                    id="plan-video-rooms"
                                    type="number"
                                    min="1"
                                    step="1"
                                    placeholder="Ex.: 5"
                                    className="input input-bordered w-full"
                                    value={features.videoMaxConcurrentRooms}
                                    onChange={e => handleFeatureChange('videoMaxConcurrentRooms', e.target.value)}
                                />
                            </div>
                            <div className="form-control w-full">
                                <label className="label" htmlFor="plan-video-minutes">
                                    <span className="label-text font-medium">Duração máxima da sessão (min)</span>
                                </label>
                                <input
                                    id="plan-video-minutes"
                                    type="number"
                                    min="1"
                                    step="1"
                                    placeholder="Ex.: 240"
                                    className="input input-bordered w-full"
                                    value={features.videoMaxRoomMinutes}
                                    onChange={e => handleFeatureChange('videoMaxRoomMinutes', e.target.value)}
                                />
                            </div>
                            <div className="form-control w-full">
                                <label className="label" htmlFor="plan-video-credits">
                                    <span className="label-text font-medium">Minutos inclusos por mês</span>
                                </label>
                                <input
                                    id="plan-video-credits"
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="Ex.: 1000"
                                    className="input input-bordered w-full"
                                    value={features.videoCredits}
                                    onChange={e => handleFeatureChange('videoCredits', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div className="form-control w-full">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={features.videoRecording}
                                    disabled={features.videoMaxParticipants.trim() === ''}
                                    onChange={e => handleFeatureChange('videoRecording', e.target.checked)}
                                />
                                <div>
                                    <span className="label-text font-medium">Gravação das reuniões</span>
                                    <p className="text-xs text-base-content/50 mt-0.5 whitespace-normal">
                                        O moderador e a API podem gravar. O minuto gravado consome créditos e o arquivo
                                        ocupa o armazenamento do cliente.
                                    </p>
                                </div>
                            </label>
                        </div>
                        <div className="form-control w-full">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={features.videoCustomDomain}
                                    disabled={features.videoMaxParticipants.trim() === ''}
                                    onChange={e => handleFeatureChange('videoCustomDomain', e.target.checked)}
                                />
                                <div>
                                    <span className="label-text font-medium">Domínio próprio na sala</span>
                                    <p className="text-xs text-base-content/50 mt-0.5 whitespace-normal">
                                        O cliente abre a sala no endereço da empresa dele (ex.: meet.empresa.com.br).
                                        Exige os limites acima preenchidos.
                                    </p>
                                </div>
                            </label>
                        </div>
                        </div>
                    </div>

                    {/* Outros */}
                    <div>
                        <h3 className="text-lg font-bold text-base-content mb-4">
                            Outros
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={features.support}
                                        onChange={e => handleFeatureChange('support', e.target.checked)}
                                    />
                                    <div>
                                        <span className="label-text font-medium">Suporte</span>
                                        <p className="text-xs text-base-content/50 mt-0.5">Acesso ao suporte técnico</p>
                                    </div>
                                </label>
                            </div>
                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-warning"
                                        checked={features.resale}
                                        onChange={e => handleFeatureChange('resale', e.target.checked)}
                                    />
                                    <div>
                                        <span className="label-text font-medium">Revenda</span>
                                        <p className="text-xs text-base-content/50 mt-0.5">Permite revender recursos</p>
                                    </div>
                                </label>
                            </div>
                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-accent"
                                        checked={features.customizations}
                                        onChange={e => handleFeatureChange('customizations', e.target.checked)}
                                    />
                                    <div>
                                        <span className="label-text font-medium">Customizações</span>
                                        <p className="text-xs text-base-content/50 mt-0.5">Personalizações sob demanda</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Tier de Módulos */}
                    <div>
                        <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                            <MdWidgets className="text-secondary" />
                            Tier e Módulos
                            {modulesLoading && (
                                <span className="loading loading-spinner loading-xs text-secondary"></span>
                            )}
                        </h3>

                        {/* Seletor de Tier */}
                        <div className="form-control w-full max-w-md mb-6">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tier Base
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={form.tierId ?? ''}
                                onChange={e => {
                                    const val = e.target.value
                                    handleChange('tierId', val ? parseInt(val, 10) : null)
                                    // Limpar módulos excluídos ao trocar tier
                                    setExcludedModules([])
                                }}
                            >
                                <option value="">Nenhum tier (somente overrides)</option>
                                {tiers.map(tier => (
                                    <option key={tier.id} value={tier.id}>
                                        {tier.name}
                                    </option>
                                ))}
                            </select>
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    O plano terá acesso aos módulos do tier selecionado
                                </span>
                            </label>
                        </div>

                        {/* Módulos do Tier */}
                        {form.tierId && tierModules.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-3">
                                    Módulos incluídos pelo Tier ({tierModules.length})
                                </h4>
                                <p className="text-sm text-base-content/50 mb-3">
                                    Desmarque módulos para excluir, ou expanda para selecionar recursos específicos
                                </p>
                                <div className="space-y-2">
                                    {tierModules.map(mod => {
                                        const isExcluded = excludedModules.includes(mod.key)
                                        const expanded = expandedTierModules.has(mod.key)
                                        const resourceCount = isExcluded ? 0 : getTierModuleResourceCount(mod)
                                        const hasOverride = additionalModules[mod.key] !== undefined

                                        return (
                                            <div
                                                key={mod.key}
                                                className={`rounded-lg border transition-all ${
                                                    isExcluded
                                                        ? 'border-error/30 bg-error/5 opacity-60'
                                                        : hasOverride
                                                            ? 'border-warning/50 bg-warning/5'
                                                            : 'border-secondary/50 bg-secondary/5'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3 p-3">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm checkbox-secondary"
                                                        checked={!isExcluded}
                                                        onChange={() => toggleExcludeModule(mod.key)}
                                                    />
                                                    <div
                                                        className="flex-1 flex items-center justify-between cursor-pointer"
                                                        onClick={() => {
                                                            if (!isExcluded) {
                                                                setExpandedTierModules(prev => {
                                                                    const next = new Set(prev)
                                                                    if (next.has(mod.key)) next.delete(mod.key)
                                                                    else next.add(mod.key)
                                                                    return next
                                                                })
                                                            }
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-sm font-medium ${isExcluded ? 'line-through text-error' : ''}`}>
                                                                {mod.label}
                                                            </span>
                                                            {!isExcluded && mod.resources.length > 0 && (
                                                                <span className={`badge badge-xs ${hasOverride ? 'badge-warning' : 'badge-secondary'}`}>
                                                                    {resourceCount}/{mod.resources.length}
                                                                </span>
                                                            )}
                                                            {hasOverride && !isExcluded && (
                                                                <span className="badge badge-xs badge-warning badge-outline">
                                                                    personalizado
                                                                </span>
                                                            )}
                                                        </div>
                                                        {!isExcluded && mod.resources.length > 0 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-ghost btn-xs"
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    setExpandedTierModules(prev => {
                                                                        const next = new Set(prev)
                                                                        if (next.has(mod.key)) next.delete(mod.key)
                                                                        else next.add(mod.key)
                                                                        return next
                                                                    })
                                                                }}
                                                            >
                                                                {expanded ? (
                                                                    <MdExpandLess className="text-lg" />
                                                                ) : (
                                                                    <MdExpandMore className="text-lg" />
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                {expanded && !isExcluded && mod.resources.length > 0 && (
                                                    <div className="px-3 pb-3 pt-0">
                                                        <div className="ml-8 grid grid-cols-2 md:grid-cols-3 gap-2">
                                                            {mod.resources.map(res => (
                                                                <label
                                                                    key={res.key}
                                                                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all text-sm ${
                                                                        isTierResourceSelected(mod.key, res.key, mod)
                                                                            ? 'bg-secondary/10'
                                                                            : 'hover:bg-base-200'
                                                                    }`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        className="checkbox checkbox-xs checkbox-secondary"
                                                                        checked={isTierResourceSelected(mod.key, res.key, mod)}
                                                                        onChange={() => toggleTierResource(mod.key, res.key, mod)}
                                                                    />
                                                                    <span>{res.label}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                                {excludedModules.length > 0 && (
                                    <p className="text-xs text-warning mt-2">
                                        {excludedModules.length} módulo(s) excluído(s) do tier
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Módulos Adicionais (override) */}
                        <div>
                            <h4 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-3">
                                Módulos Adicionais (Override)
                            </h4>
                            <p className="text-sm text-base-content/50 mb-3">
                                Módulos exclusivos ou de outros tiers que este plano deve ter acesso
                            </p>

                            {availableForAdditional.length === 0 && form.tierId ? (
                                <p className="text-sm text-base-content/40 italic">
                                    Todos os módulos já estão incluídos pelo tier
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {availableForAdditional.map(mod => {
                                        const selected = mod.key in additionalModules
                                        const expanded = expandedAdditional.has(mod.key)
                                        const resourceCount = selected
                                            ? (additionalModules[mod.key].length === 0
                                                ? mod.resources.length
                                                : additionalModules[mod.key].length)
                                            : 0

                                        return (
                                            <div
                                                key={mod.key}
                                                className={`rounded-lg border transition-all ${selected
                                                    ? 'border-success/50 bg-success/5'
                                                    : 'border-base-300'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3 p-3">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm checkbox-success"
                                                        checked={selected}
                                                        onChange={() => toggleAdditionalModule(mod)}
                                                    />
                                                    <div
                                                        className="flex-1 flex items-center justify-between cursor-pointer"
                                                        onClick={() => {
                                                            setExpandedAdditional(prev => {
                                                                const next = new Set(prev)
                                                                if (next.has(mod.key)) next.delete(mod.key)
                                                                else next.add(mod.key)
                                                                return next
                                                            })
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium">{mod.label}</span>
                                                            {mod.tiers && mod.tiers.length > 0 ? (
                                                                mod.tiers.map(t => (
                                                                    <span
                                                                        key={t.id}
                                                                        className="badge badge-xs badge-secondary"
                                                                    >
                                                                        {t.name}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="badge badge-xs badge-warning badge-outline">
                                                                    Sem tier
                                                                </span>
                                                            )}
                                                            {selected && mod.resources.length > 0 && (
                                                                <span className="badge badge-success badge-xs">
                                                                    {resourceCount}/{mod.resources.length}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {mod.resources.length > 0 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-ghost btn-xs"
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    setExpandedAdditional(prev => {
                                                                        const next = new Set(prev)
                                                                        if (next.has(mod.key)) next.delete(mod.key)
                                                                        else next.add(mod.key)
                                                                        return next
                                                                    })
                                                                }}
                                                            >
                                                                {expanded ? (
                                                                    <MdExpandLess className="text-lg" />
                                                                ) : (
                                                                    <MdExpandMore className="text-lg" />
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                {expanded && mod.resources.length > 0 && (
                                                    <div className="px-3 pb-3 pt-0">
                                                        <div className="ml-8 grid grid-cols-2 md:grid-cols-3 gap-2">
                                                            {mod.resources.map(res => (
                                                                <label
                                                                    key={res.key}
                                                                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all text-sm ${
                                                                        isAdditionalResourceSelected(mod.key, res.key)
                                                                            ? 'bg-success/10'
                                                                            : 'hover:bg-base-200'
                                                                    } ${!selected ? 'opacity-40 pointer-events-none' : ''}`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        className="checkbox checkbox-xs checkbox-success"
                                                                        checked={isAdditionalResourceSelected(mod.key, res.key)}
                                                                        onChange={() => toggleAdditionalResource(mod.key, res.key, mod)}
                                                                        disabled={!selected}
                                                                    />
                                                                    <span>{res.label}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Resumo */}
                        {(form.tierId || Object.keys(additionalModules).length > 0) && (
                            <div className="mt-4 p-4 bg-info/10 border border-info/20 rounded-xl">
                                <p className="text-sm text-info-content/80">
                                    <strong>Resumo:</strong>{' '}
                                    {tierModules.length - excludedModules.length} módulo(s) do tier
                                    {Object.keys(additionalModules).length > 0 && (
                                        <> + {Object.keys(additionalModules).length} adicional(is)</>
                                    )}
                                    {excludedModules.length > 0 && (
                                        <> - {excludedModules.length} excluído(s)</>
                                    )}
                                    {' '}= {tierModules.length - excludedModules.length + Object.keys(additionalModules).length} módulo(s) total
                                </p>
                            </div>
                        )}

                        {!form.tierId && Object.keys(additionalModules).length === 0 && (
                            <div className="mt-3 text-sm text-warning">
                                Nenhum tier selecionado e nenhum módulo adicional. Empresas com este plano não terão acesso a nenhum módulo.
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/plans')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <span className="loading loading-spinner loading-sm" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                {isEdit
                                    ? 'Salvar Alterações'
                                    : 'Criar Plano'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
